/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosButton,
	CosmosCard,
	CosmosRichText,
	CosmosTextVariantEnum,
	hasContent,
	TextLinkAndChevron,
	TextLinkAndChevronProps,
} from "@components/elements";
import { CosmosElementLearnDocumentationCardFragment } from "@cosmos/hygraph-schema";
import { cx, css, cva } from "@panda/css";
import { Box } from "@panda/jsx";

const cardStyle = cva({
	base: {
		rowGap: "eds.dimension.16",
		columnGap: "eds.dimension.32",
		justifyContent: "space-between",
		width: "full",
		"&&": {
			// TODO: might not be needed, migrated from previous design
			bgColor: "eds.background.elevated.high.default",
		},
	},
	variants: {
		type: {
			fullWidth: {
				xlDown: {
					flexDir: "row",
					alignItems: "flex-start",

					"& > *:nth-child(1)": { flexGrow: "1" },
					"& > *:nth-child(2)": { flexShrink: "0" },
				},
				mdDown: {
					flexDir: "column",
				},
			},
		},
	},
});

export const linksStyle: string = css({
	display: "flex",
	flexDir: "column",
	flexWrap: "nowrap",
	alignItems: "flex-start",
	rowGap: "eds.dimension.8",
	columnGap: "eds.dimension.8",
});

export type DocumentCardProps = CosmosElementLearnDocumentationCardFragment & { fullWidth?: boolean };

export function DocumentCard({ description, links = [], cta, fullWidth }: DocumentCardProps): React.JSX.Element | null {
	const hasDescription = hasContent(description);
	if (!hasDescription && !links.length) {
		return null;
	}
	return (
		<CosmosCard
			padding={24}
			className={cx("document-card", cardStyle({ type: fullWidth ? "fullWidth" : undefined }))}
			variant="material"
			direction="column"
		>
			<CosmosCard.Content rowGap="eds.dimension.16" display="flex" flexDirection="column">
				{hasDescription ? <CosmosRichText variant="paragraphSM" {...description} /> : null}
				{links?.length > 0 ? (
					<Box className={cx("links", linksStyle)}>
						{links.map((link: TextLinkAndChevronProps, i) => (
							<TextLinkAndChevron key={`link-${i}`} textVariant={CosmosTextVariantEnum.paragraphSM} {...link} />
						))}
					</Box>
				) : null}
			</CosmosCard.Content>
			<CosmosButton {...cta} className={"cta"} />
		</CosmosCard>
	);
}
